import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface Dashboard {
  kpis: {
    totalAdvertisements: number;
    totalVendors: number;
    totalCampaigns: number;
    totalActiveTerritories: number;
    territoriesCovered: number;
    coveragePct: number;
    dryTerritories: number;
  };
  indices: Record<string, number>;
  mediumWiseCoverage: Array<{ mediumCategory: string; adCount: number; distinctTerritoryCount: number; distinctVendorCount: number }>;
  topVendors: Array<{ vendorId: string; vendorName: string | null; adCount: number; performanceIndexPct: number }>;
  topTerritories: Array<{ geographyId: string; geographyName: string | null; adCount: number }>;
}
interface Recommendations {
  nextAreasToTarget: Array<{ territoryId: string; name: string | null; adCount: number; levels: number[] }>;
  mediumsToDeploy: { unusedOrLowMediums: string[]; note: string };
  vendorsToExpand: Array<{ vendorId: string; name: string | null; performanceIndexPct: number; distinctTerritoryCount: number }>;
  underpenetratedDistricts: Array<{ districtId: string; name: string | null; adCount: number }>;
  villagesZeroVisibility: { available: boolean; reason: string };
}

/** §17 executive dashboard + §18 indices + §19 AI recommendations. */
export function ExecutiveDashboardScreen(): ReactNode {
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [dash, setDash] = useState<Dashboard | null>(null);
  const [rec, setRec] = useState<Recommendations | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    apiRequest<{ data: Dashboard }>('/v1/geo-intel/executive/dashboard', { token })
      .then((r) => { if (alive) setDash(r.data); })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof ApiError && e.status === 403
          ? t('m16report.exec.forbidden')
          : (e as Error).message);
      })
      .finally(() => { if (alive) setLoading(false); });
    apiRequest<{ data: Recommendations }>('/v1/geo-intel/executive/recommendations', { token })
      .then((r) => { if (alive) setRec(r.data); })
      // Without RECOMMENDATION_VIEW the section is simply not this reader's;
      // any other failure is a failure and is said.
      .catch((e) => {
        if (alive && !(e instanceof ApiError && e.status === 403)) setError((e as Error).message);
      });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="executive-dashboard">
      <h1>{t('m16report.exec.title')}</h1>
      {error && <p role="alert" aria-label="exec-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : dash && (
        <>
          <section aria-label="exec-kpis" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <span aria-label="kpi-ads">{t('m16report.exec.kpi.ads', { value: dash.kpis.totalAdvertisements })}</span>
            <span aria-label="kpi-vendors">{t('m16report.exec.kpi.vendors', { value: dash.kpis.totalVendors })}</span>
            <span aria-label="kpi-campaigns">{t('m16report.exec.kpi.campaigns', { value: dash.kpis.totalCampaigns })}</span>
            <span aria-label="kpi-territories">{t('m16report.exec.kpi.territories', { covered: dash.kpis.territoriesCovered, total: dash.kpis.totalActiveTerritories })}</span>
            <span aria-label="kpi-coverage">{t('m16report.exec.kpi.coverage', { value: dash.kpis.coveragePct })}</span>
            <span aria-label="kpi-dry">{t('m16report.exec.kpi.dry', { value: dash.kpis.dryTerritories })}</span>
          </section>

          <section aria-label="exec-medium-coverage">
            <h2>{t('m16report.exec.medium.title')}</h2>
            <DataTable
              ariaLabel="medium-coverage-table"
              emptyMessage={t('m16report.exec.nodata')}
              rowKey={(m) => m.mediumCategory}
              rows={dash.mediumWiseCoverage}
              columns={[
                { key: 'medium', header: t('m16report.col.medium'), render: (m) => m.mediumCategory },
                { key: 'ads', header: t('m16report.col.ads'), render: (m) => m.adCount },
                { key: 'territories', header: t('m16report.col.territories'), render: (m) => m.distinctTerritoryCount },
                { key: 'vendors', header: t('m16report.col.vendors'), render: (m) => m.distinctVendorCount },
              ]}
            />
          </section>

          <section aria-label="exec-rankings">
            <h2>{t('m16report.exec.topVendors')}</h2>
            <ol aria-label="top-vendors">
              {dash.topVendors.map((v) => <li key={v.vendorId}>{v.vendorName ?? '—'} — {v.adCount} ads ({number(v.performanceIndexPct)}%)</li>)}
            </ol>
            <h2>{t('m16report.exec.topTerritories')}</h2>
            <ol aria-label="top-territories">
              {dash.topTerritories.map((tr) => <li key={tr.geographyId}>{tr.geographyName ?? '—'} — {tr.adCount} ads</li>)}
            </ol>
          </section>

          {rec && (
            <section aria-label="exec-recommendations">
              <h2>{t('m16report.exec.rec.title')}</h2>
              <h3>{t('m16report.exec.rec.areas')}</h3>
              <ul aria-label="rec-areas">
                {rec.nextAreasToTarget.length === 0 ? <li>{t('m16report.exec.rec.none')}</li> : rec.nextAreasToTarget.map((a) => (
                  <li key={a.territoryId}>{a.name ?? '—'} — {a.adCount} ads (levels {a.levels.join(', ') || 'covered'})</li>
                ))}
              </ul>
              <h3>{t('m16report.exec.rec.mediums')}</h3>
              <p aria-label="rec-mediums">{rec.mediumsToDeploy.unusedOrLowMediums.join(', ') || t('m16report.exec.rec.mediums.all')}</p>
              <h3>{t('m16report.exec.rec.vendors')}</h3>
              <ul aria-label="rec-vendors">
                {rec.vendorsToExpand.map((v) => <li key={v.vendorId}>{v.name ?? '—'} ({number(v.performanceIndexPct)}%, {v.distinctTerritoryCount} territories)</li>)}
              </ul>
              <h3>{t('m16report.exec.rec.districts')}</h3>
              <ul aria-label="rec-districts">
                {rec.underpenetratedDistricts.map((d) => <li key={d.districtId}>{d.name ?? '—'} — {d.adCount} ads</li>)}
              </ul>
              <p aria-label="rec-villages">{rec.villagesZeroVisibility.reason}</p>
            </section>
          )}
        </>
      )}
    </main>
  );
}
